import React, { useState, useEffect, useContext } from 'react';
import { Select, Space, Typography, Button, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { useRouter } from 'next/router';
import { ThemeContext } from 'pages/_app';
import {
  useUserScenesQuery,
  useUpdateUserSceneAssetsMutation,
  useFindManyUserSceneAssetsQuery,
} from '@src/components/generated/graphql';
import { openNotification } from 'util/helpers';
import { ObjectPortalPropsProps } from 'src/types';
import styles from './object-portal-props.module.css';

export default function ObjectPortalProps({
  portal,
  userId,
}: ObjectPortalPropsProps) {
  const { isDark } = useContext(ThemeContext);
  const router = useRouter();
  const [selectedSceneId, setSelectedSceneId] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [options, setOptions] = useState<
    Array<{ value: string; label: string }>
  >([]);

  const currentSceneId = Array.isArray(router.query?.id)
    ? router.query.id[0]
    : router.query?.id;

  const [updateAsset] = useUpdateUserSceneAssetsMutation();

  const { data: scenes, loading: loadScenes } = useUserScenesQuery({
    variables: {
      where: {
        userId: {
          equals: userId,
        },
      },
    },
    fetchPolicy: 'no-cache',
  });

  const { data: portalAsset } = useFindManyUserSceneAssetsQuery({
    variables: {
      where: {
        id: {
          equals: portal?.props?.asset?.id,
        },
      },
    },
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    if (scenes?.userScenes.length) {
      const currentPortalSceneId =
        portalAsset?.findManyUserSceneAssets?.[0]?.portalSceneId;

      const arr = scenes.userScenes
        .filter((e) => e.id !== currentSceneId)
        .map((e) => ({
          value: e.id,
          label: e.name,
        }));

      setOptions(() => arr);

      if (currentPortalSceneId && !selectedSceneId) {
        setSelectedSceneId(currentPortalSceneId);
      }
    }
  }, [
    scenes?.userScenes,
    currentSceneId,
    userId,
    portalAsset?.findManyUserSceneAssets,
    selectedSceneId,
  ]);

  const handleSavePortal = async (): Promise<void> => {
    if (!selectedSceneId || !portal?.props?.asset?.id) {
      openNotification(
        'Error',
        'Please select a scene and save the portal first',
        'top',
        2,
        'error'
      );
      return;
    }

    setLoading(true);
    try {
      await updateAsset({
        variables: {
          where: {
            id: portal.props.asset.id,
          },
          data: {
            portalSceneId: {
              set: selectedSceneId,
            },
          },
        },
      });
      openNotification(
        'Success',
        'Successfully saved portal configuration',
        'top',
        2,
        'success'
      );
    } catch (error) {
      openNotification(
        'Error',
        'Failed to save portal configuration',
        'top',
        2,
        'error'
      );
    }
    setLoading(false);
  };

  return (
    <div className={isDark ? styles.darkTheme : ''}>
      <Space direction='vertical' size='large' className={styles.container}>
        <div>
          <Typography.Text strong>Target Scene</Typography.Text>
          <Select
            placeholder='Choose the scene to portal to'
            options={options}
            value={selectedSceneId}
            onChange={setSelectedSceneId}
            style={{ width: '100%', marginTop: 8 }}
            loading={loadScenes}
            className={styles.portalSelect}
          />
          {selectedSceneId && (
            <Typography.Text
              type='secondary'
              style={{ fontSize: '12px', marginTop: 4 }}
            >
              Currently selected:{' '}
              {scenes?.userScenes.find((scene) => scene.id === selectedSceneId)
                ?.name || 'Unknown scene'}
            </Typography.Text>
          )}
        </div>

        <div>
          <Typography.Text type='secondary'>
            Select the scene that users will be transported to when they
            interact with this portal.
          </Typography.Text>
        </div>

        <div>
          <Button
            type='primary'
            onClick={handleSavePortal}
            loading={loading}
            disabled={!selectedSceneId || !portal?.props?.asset?.id}
            style={{ width: '100%' }}
          >
            {loading ? (
              <Spin
                indicator={
                  <LoadingOutlined
                    style={{ fontSize: 16, color: '#fff' }}
                    spin
                  />
                }
              />
            ) : (
              'Save Portal Configuration'
            )}
          </Button>
        </div>
      </Space>
    </div>
  );
}
